/**
 * Tasks — universal BlockingTask inbox.
 *
 * Replaces the three separate Todos surfaces from the legacy portal
 * (campus / kitchen / kiosk) with a single permission-gated inbox.
 * The nav resolver in `@ordo/access` hides this page from PARENT /
 * STUDENT / TEACHER profiles, so we don't filter here.
 *
 * Rendered with the shared `@ordo/ui` `TaskList` (same pattern as
 * Tasker / Menus): List/Board toggle + search, tasks grouped into
 * collapsible Overdue / Due Today / Due Later / No Due Date sections.
 * Clicking a row opens the workflow where the action happens
 * (`workflowRouteForTask`) — the full-page temperature-log surface for
 * chefs, the Production Records wizard for the school's Transport logs /
 * Service records tasks (PROD-1277) — or, for subKinds without a dedicated
 * workflow page, the `<TaskDetailDrawer>` sheet, which is also where
 * dismissal lives.
 *
 * The complete-circle is intentionally inert: blocking tasks clear when
 * their underlying work is done, not by toggling a checkbox.
 */

import { Button } from '@ordo/ui/button'
import { EmptyState } from '@ordo/ui/empty-state'
import {
  TaskList,
  type TaskListItem,
  type TaskListSection,
  type TaskListTone,
  type TaskListView,
} from '@ordo/ui/task-list'
import { CheckCircle2, ListChecks } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { type MyTaskRow, type TaskFilter, useMyTasksQuery, variablesForFilter } from '@/api/tasks'
import { OrdoPageContainer } from '@/components/ordo-page-container'
import { OrdoPageHeader } from '@/components/ordo-page-header'
import {
  bucketTasksByDue,
  buildTaskRollups,
  computeTaskDisplayPriority,
  displayTitleForTask,
  type DueBuckets,
  filterTasksBySearch,
  formatRelativeDate,
  isSchoolProductionTask,
  isTaskRollupRow,
  mergeChefTempLogDayRows,
  onboardingTagFor,
  type TaskRollupRow,
  workflowRouteForTask,
} from '@/components/tasks/helpers'
import { TaskDetailDrawer } from '@/components/tasks/task-detail-drawer'
import { TaskRollupDrawer } from '@/components/tasks/task-rollup-drawer'
import { useChefTempLogTasks } from '@/lib/chefs/temp-log'
import { TasksSkeleton } from '@/skeletons/tasks-skeleton'
import { useProfile } from '@/stores/profile'

import { emptyStateDescription, emptyStateTitle } from './tasks-empty-state'

function isChefProfile(codes: string[] | undefined): boolean {
  return (codes ?? []).some((c) => c.startsWith('CHEF'))
}

/** Tone for a task's due label, by how soon it's due. */
function dueToneFor(dueAt: string | null): TaskListTone {
  if (!dueAt) {
    return 'muted'
  }
  switch (computeTaskDisplayPriority(dueAt)) {
    case 'overdue':
      return 'danger'
    case 'due_today':
    case 'due_tomorrow':
      return 'warning'
    default:
      return 'muted'
  }
}

function toTaskListItem(task: MyTaskRow): TaskListItem {
  const tag = onboardingTagFor(task)
  const completed = task.status === 'COMPLETED'
  return {
    id: task.id,
    title: task.title,
    completed,
    // Completion clears blocking tasks via their underlying work, not a
    // checkbox — the circle is display-only.
    completionDisabled: true,
    dueLabel: completed ? null : task.dueAt ? formatRelativeDate(task.dueAt) : null,
    dueTone: dueToneFor(task.dueAt),
    // No org/school name chip: a school admin's inbox is already scoped to their
    // school (and the sidebar campus selector shows which), so per-row school
    // names are pure noise.
    tagLabel: tag?.label ?? null,
    tagTone: tag?.tone,
    commentCount: task.commentCount,
  }
}

// Section order + labels for the four due-date buckets.
const SECTION_DEFS: Array<{ id: string; title: string; key: keyof DueBuckets }> = [
  { id: 'overdue', title: 'Overdue', key: 'overdue' },
  { id: 'due-today', title: 'Due Today', key: 'dueToday' },
  { id: 'due-later', title: 'Due Later', key: 'dueLater' },
  { id: 'no-due-date', title: 'No Due Date', key: 'noDueDate' },
]

export function TasksPage() {
  // Filter is fixed to 'all' for now — the chip strip + popover are
  // overkill when most users have ≤3 open tasks at a time. Re-introduce
  // when (a) backend churn grows the list AND (b) we have a real
  // taxonomy worth filtering by.
  const filter: TaskFilter = 'all'
  const navigate = useNavigate()
  const [view, setView] = useState<TaskListView>('list')
  const [search, setSearch] = useState('')
  // Drawer is only the fallback for subKinds without a dedicated
  // workflow page (and for school tasks projected without shipment meta).
  const [selectedTask, setSelectedTask] = useState<MyTaskRow | null>(null)
  // The collapsed board ticket being stepped through campus-by-campus.
  const [selectedRollup, setSelectedRollup] = useState<TaskRollupRow | null>(null)

  // Everyone reads the real BlockingTask inbox. For chefs the raw
  // per-shipment CHEF_SUBMIT_TEMP_LOG rows are replaced by the
  // synthesized one-row-per-day temp-log tasks (`/api/temp-log/days`),
  // so the day-grouped workflow UX is preserved without double rows.
  const { profile } = useProfile()
  const isChef = isChefProfile(profile?.profileCodes)
  const chefTempLog = useChefTempLogTasks(isChef)

  const variables = useMemo(() => variablesForFilter(filter), [filter])
  const realQuery = useMyTasksQuery(variables)

  const tasks = useMemo(() => {
    const real = realQuery.data?.tasks ?? []
    const merged = isChef ? mergeChefTempLogDayRows(real, chefTempLog.tasks) : real
    // School shipment tasks render their subKind label (Transport logs /
    // Service records) whatever title the stored row carries, so search,
    // rollups and the list all see the same name.
    return merged.map((task) =>
      task.title === displayTitleForTask(task) ? task : { ...task, title: displayTitleForTask(task) },
    )
  }, [isChef, chefTempLog.tasks, realQuery.data])
  const error = realQuery.error ?? (isChef ? chefTempLog.error : null)

  // Collapse per-campus shipment tasks into one board ticket per
  // day / mealtime / drop BEFORE search + bucketing, so the sections
  // count tickets, not raw rows.
  const boardRows = useMemo(() => buildTaskRollups(tasks), [tasks])
  const filtered = useMemo(() => filterTasksBySearch(boardRows, search), [boardRows, search])
  const sections = useMemo<TaskListSection[]>(() => {
    // Completed tasks (today only onboarding rows reach the inbox completed)
    // get their own collapsed group so a school sees their finished work
    // instead of it vanishing; open tasks keep the due-date buckets.
    const openTasks = filtered.filter((t) => t.status !== 'COMPLETED')
    const completedTasks = filtered.filter((t) => t.status === 'COMPLETED')
    const buckets = bucketTasksByDue(openTasks)
    const dueSections = SECTION_DEFS.map((def) => ({ def, items: buckets[def.key] }))
      .filter(({ items }) => items.length > 0)
      .map(({ def, items }) => ({
        id: def.id,
        title: def.title,
        count: items.length,
        items: items.map(toTaskListItem),
      }))
    if (completedTasks.length > 0) {
      dueSections.push({
        id: 'completed',
        title: 'Completed',
        count: completedTasks.length,
        items: completedTasks.map(toTaskListItem),
      })
    }
    return dueSections
  }, [filtered])
  const taskById = useMemo(() => new Map(filtered.map((t) => [t.id, t] as const)), [filtered])

  if ((realQuery.isPending && !realQuery.data) || (isChef && chefTempLog.isPending)) {
    return <TasksSkeleton />
  }

  function openTask(task: MyTaskRow) {
    if (isTaskRollupRow(task)) {
      // A school day ticket (Transport logs / Service records, one campus,
      // one service day) IS one Production Records wizard — go there via
      // its first member's shipment meta (PROD-1277).
      if (isSchoolProductionTask(task.subKind)) {
        const member = task.rollupTasks.find((t) => workflowRouteForTask(t)) ?? null
        const route = member ? workflowRouteForTask(member) : null
        if (route) {
          navigate(route)
          return
        }
      }
      // Other rollup tickets open the step-through wizard, never a workflow
      // route (their synthetic ids would 404 the per-task workflow pages).
      setSelectedRollup(task)
      return
    }
    const route = workflowRouteForTask(task)
    if (route) {
      navigate(route)
      return
    }
    setSelectedTask(task)
  }

  return (
    // `md:px-6` (not the OrdoPageContainer default `md:px-4`) so the desktop
    // horizontal padding matches the full-bleed `-mx-6` the collapsible
    // `TaskList` applies to its rows. With `md:px-4` the rows overflow 0.5rem
    // past each edge and the page scrolls horizontally.
    <OrdoPageContainer className="md:px-6">
      <OrdoPageHeader icon={ListChecks} title="Tasks" />

      {error ? (
        <ErrorBanner message={error.message} onRetry={() => realQuery.refetch()} />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 />}
          title={emptyStateTitle(filter)}
          description={emptyStateDescription(filter)}
        />
      ) : (
        <>
          <TaskList
            view={view}
            onViewChange={setView}
            sectionStyle="collapsible"
            searchValue={search}
            onSearchChange={setSearch}
            sections={sections}
            onItemSelect={(id) => {
              const task = taskById.get(id)
              if (task) {
                openTask(task)
              }
            }}
          />
          {sections.length === 0 ? (
            <p className="px-1 py-6 text-sm text-muted-foreground">No tasks match your search.</p>
          ) : null}
        </>
      )}

      <TaskDetailDrawer task={selectedTask} onClose={() => setSelectedTask(null)} />
      <TaskRollupDrawer rollup={selectedRollup} onClose={() => setSelectedRollup(null)} />
    </OrdoPageContainer>
  )
}

// Re-export so existing call sites importing from `@/pages/tasks`
// keep working. The strings themselves live in `tasks-empty-state.ts`
// so unit tests can import without the React/TanStack dep tree.
export { emptyStateDescription, emptyStateTitle }

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-medium text-destructive">Couldn&apos;t load your tasks.</p>
        <p className="mt-1 text-xs text-muted-foreground">{message}</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  )
}
